import { useRouter } from "next/router";
import type { FunctionComponent, MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { pathname as plusSearchPathname } from "scmp-app/pages/plus/search";
import type { entrySearchExploreKeyAnglesItemEntryContent$key } from "scmp-app/queries/__generated__/entrySearchExploreKeyAnglesItemEntryContent.graphql";

import { ArticleItem, Headline, PlusFlag, PublishDate, StyledEntityLink } from "./styles";

type Props = {
  categoryName: string;
  categoryPosition: number;
  reference: entrySearchExploreKeyAnglesItemEntryContent$key;
  resultPosition: number;
};

export const SearchExploreKeyAnglesItemEntry: FunctionComponent<Props> = ({
  categoryName,
  categoryPosition,
  reference,
  resultPosition,
}) => {
  const content = useFragment(
    graphql`
      fragment entrySearchExploreKeyAnglesItemEntryContent on Content {
        entityId
        headline
        publishedDate
        ...entityLink
        ...helpersCheckIsPlusArticle
        ...helpersExpertPerspectiveArticle
        ...hooksContentItemProvidersContent @arguments(withHeadline: true, withPublishedDate: true)
        ...hooksPlusArticleLinkHandlerArticle
      }
    `,
    reference,
  );

  const { route } = useRouter();
  const { handleOpen } = usePlusArticlePopup();
  const providers = useContentItemProviders(content);
  const isPlusArticle = useMemo(() => checkIsPlusArticle(content), [content]);
  const isOnPlusSearch = useMemo(() => route === plusSearchPathname, [route]);

  const { currentValue, initialSearchId, refineSearch } = useSearchContext();

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const getOrderBy = () => {
        if (refineSearch.orderBy?.field === "PUBLISHED_DATE") {
          switch (refineSearch.orderBy.direction) {
            case "ASC":
              return "oldest";
            case "DESC":
              return "newest";
          }
        }
        return "relevance";
      };

      const getSection = () => {
        if (!notEmpty(refineSearch.sectionIds)) return "All";
        return refineSearch.sectionIds.join(",");
      };

      // GA4 21.2.1
      sendGA4Tracking({
        action: "click",
        category: "search",
        customized_parameters: {
          article_id: content.entityId,
          article_type: refineSearch.articleTypeText,
          date_range: refineSearch.dateRangeText,
          is_plus_article: isPlusArticle ? "yes" : "no",
          search_id: initialSearchId ?? "",
          search_text: currentValue,
          section: getSection(),
          sort: getOrderBy(),
          widget_category_name: categoryName,
          widget_category_position: categoryPosition,
          widget_name: "Explore key angles",
          widget_result_position: resultPosition,
        },
        subcategory: "result",
      } as SearchEvent);

      if (!isOnPlusSearch) return;
      event.preventDefault();
      handleOpen(content);
    },
    [
      categoryName,
      categoryPosition,
      content,
      currentValue,
      handleOpen,
      initialSearchId,
      isOnPlusSearch,
      isPlusArticle,
      refineSearch,
      resultPosition,
    ],
  );

  return (
    <ArticleItem key={content.entityId}>
      {isPlusArticle && <PlusFlag aria-label="Plus" role="img" />}
      <PublishDate>{providers.publishedDate({ format: "dd MMM yyyy" })}</PublishDate>
      <Headline>
        <StyledEntityLink onClick={handleClick} reference={content} target="_blank">
          {providers.headline()}
        </StyledEntityLink>
      </Headline>
    </ArticleItem>
  );
};

SearchExploreKeyAnglesItemEntry.displayName = "SearchExploreKeyAnglesItemEntry";
